import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Button, Modal, ModalHeader, ModalBody, FormGroup, Input, Label } from 'reactstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import axios from 'axios';
import Loading from '../../components/Loading/Loading';
import DataServiceNotificationForm from '../../components/DataServiceNotificationForm';

const EVENT_CODE_OPTIONS = [
  { value: '', label: 'All Event Codes' },
  { value: 'SN_VACANT', label: 'SN-Vacant' },
  { value: 'SN_NO_COMMS', label: 'SN-NoComms' },
  { value: 'SN_REMOTE_ENABLED', label: 'SN-RemoteEnabled' },
  { value: 'SN_REMOTE_DISABLED', label: 'SN-RemoteDisabled' },
  { value: 'SN_OFF_PEAK_DEC', label: 'SN-OffPeakDec' },
];

const DataServiceNotificationManagement = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalSize, setTotalSize] = useState(0);
  const [sortBy, setSortBy] = useState('created');
  const [sortDir, setSortDir] = useState('desc');

  // Filter state
  const [meterPointFilter, setMeterPointFilter] = useState('');
  const [debouncedMeterPointFilter, setDebouncedMeterPointFilter] = useState('');
  const [eventCodeFilter, setEventCodeFilter] = useState('');

  // Debounce logic for meter point filter
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMeterPointFilter(meterPointFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [meterPointFilter]);

  // Fetch data when filters or pagination changes
  useEffect(() => {
    fetchData();
  }, [page, size, sortBy, sortDir, debouncedMeterPointFilter, eventCodeFilter]);

  // Transform event code from UI format to API format
  const transformEventCodeForAPI = (eventCode) => {
    if (!eventCode) return '';
    // Convert "SN-Vacant" to "SN_VACANT", "SN-NoComms" to "SN_NO_COMMS", etc.
    return eventCode
      .replace(/-/g, '_')
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .toUpperCase();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.token;
      const params = {
        page,
        size,
        sortBy,
        sortDir,
      };

      if (debouncedMeterPointFilter) {
        params.meterPoint = debouncedMeterPointFilter;
      }

      if (eventCodeFilter) {
        params.eventCode = transformEventCodeForAPI(eventCodeFilter);
      }

      const response = await axios.get('/df/dataflows/rest/v1/data-service-notification/data', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      setTableData(response.data.content || []);
      setTotalSize(response.data.totalElements || 0);
    } catch (error) {
      // Error fetching data
      setTableData([]);
      setTotalSize(0);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSuccess = () => {
    setIsModalOpen(false);
    // Refresh table data after successful submission
    fetchData();
  };

  // Format date for display
  const formatDate = (cell) => (cell ? moment(cell).format('DD/MM/YYYY') : '');

  // Format JSON content for display
  const formatContent = (cell) => {
    if (!cell) return '';
    try {
      return <pre style={{ margin: 0, overflow: 'auto', fontSize: '12px' }}>{JSON.stringify(cell, null, 2)}</pre>;
    } catch (e) {
      return String(cell);
    }
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        dataField: 'meterPoint',
        text: 'MPAN Core',
        sort: true,
        headerStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.2',
        },
      },
      {
        dataField: 'eventCode',
        text: 'Event Code',
        sort: true,
        headerStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.2',
        },
      },
      {
        dataField: 'supplierNotificationEffectiveFromDate',
        text: 'Effective From Date',
        sort: true,
        formatter: formatDate,
        headerStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.2',
        },
      },
      {
        dataField: 'content',
        text: 'Content',
        sort: false,
        formatter: formatContent,
        headerStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.2',
        },
      },
    ],
    []
  );

  // Handle table change (pagination, sorting)
  const handleTableChange = (type, { page: newPage, sizePerPage, sortField, sortOrder }) => {
    if (type === 'pagination') {
      setPage(newPage - 1); // react-bootstrap-table uses 1-based indexing
      setSize(sizePerPage);
    } else if (type === 'sort') {
      setSortBy(sortField);
      setSortDir(sortOrder);
    }
  };

  // Pagination options
  const paginationOptions = {
    page: page + 1, // react-bootstrap-table uses 1-based indexing
    sizePerPage: size,
    totalSize,
    sizePerPageList: [
      { text: '10', value: 10 },
      { text: '25', value: 25 },
      { text: '50', value: 50 },
      { text: '100', value: 100 },
    ],
  };

  return (
    <div className='animated fadeIn'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Data Service Notification Management</h1>
        </Col>
        <Col className='col-4'>
          <Button
            className='pull-right'
            color='primary'
            style={{ marginTop: '5px', padding: '8px 25px' }}
            onClick={toggleModal}
          >
            <i className='icon ion-plus-round'></i>&nbsp;&nbsp;Submit New Notification
          </Button>
        </Col>
      </Row>

      <Modal isOpen={isModalOpen} toggle={toggleModal} size='lg' style={{ maxWidth: '800px', width: '90%' }}>
        <ModalHeader toggle={toggleModal}>Submit Data Service Notification</ModalHeader>
        <ModalBody>
          <DataServiceNotificationForm onClose={toggleModal} onSuccess={handleSuccess} />
        </ModalBody>
      </Modal>
      <div className='page-cnt'>
        {/* Filters */}
        <Row className='mt-3'>
          <Col md='6'>
            <FormGroup>
              <Label for='meterPointFilter'>Filter by Meter Point</Label>
              <Input
                type='text'
                id='meterPointFilter'
                placeholder='Search by meter point...'
                value={meterPointFilter}
                onChange={(e) => setMeterPointFilter(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md='6'>
            <FormGroup>
              <Label for='eventCodeFilter'>Filter by Event Code</Label>
              <Input
                type='select'
                id='eventCodeFilter'
                value={eventCodeFilter}
                onChange={(e) => setEventCodeFilter(e.target.value)}
              >
                {EVENT_CODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        {/* Table */}
        <Row className='mt-3'>
          <Col>
            {loading ? (
              <Loading />
            ) : (
              <BootstrapTable
                classes='table-hover'
                wrapperClasses='table-responsive'
                keyField='id'
                data={tableData}
                columns={columns}
                remote
                onTableChange={handleTableChange}
                pagination={paginationFactory(paginationOptions)}
                bordered={false}
                hover
                noDataIndication={() => <div className='text-center p-3'>No notifications found</div>}
              />
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DataServiceNotificationManagement;
